import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { makeCurrentUserAdmin, checkAdminStatus } from '../../utils/makeAdmin';
import './AdminSetup.css';

const AdminSetup = () => {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [adminStatus, setAdminStatus] = useState(false);
  const [setupAvailable, setSetupAvailable] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkSetupAvailability = async () => {
      if (!auth.currentUser) {
        setMessage('Please log in first');
        setIsError(true);
        setLoading(false);
        return;
      }

      try {
        // First check if current user is already an admin
        const status = await checkAdminStatus();
        setAdminStatus(status.isAdmin);
        
        // Then check if any admins exist in the system
        const adminsQuery = query(collection(db, 'users'), where('isAdmin', '==', true));
        const querySnapshot = await getDocs(adminsQuery);
        
        // Setup is only available if no admins exist yet
        const noAdminsExist = querySnapshot.empty;
        setSetupAvailable(noAdminsExist);
        
        if (!noAdminsExist && !status.isAdmin) {
          setMessage('Admin setup is no longer available. Please contact an existing administrator.');
          setIsError(true);
        }
      } catch (error) {
        console.error('Error checking setup availability:', error);
        setMessage('Error checking setup availability: ' + error.message);
        setIsError(true);
      } finally {
        setLoading(false);
      }
    };

    checkSetupAvailability();
  }, []);

  const handleMakeAdmin = async () => {
    setLoading(true);
    setMessage('');
    setIsError(false);

    try {
      // Double-check that no admins exist before proceeding
      const adminsQuery = query(collection(db, 'users'), where('isAdmin', '==', true));
      const querySnapshot = await getDocs(adminsQuery);
      
      if (!querySnapshot.empty) {
        setMessage('Admin setup is no longer available. Another user has already been made admin.');
        setIsError(true);
        setSetupAvailable(false);
        setLoading(false);
        return;
      }
      
      const result = await makeCurrentUserAdmin();
      
      if (result.success) {
        setMessage(result.message);
        setAdminStatus(true);
        setSetupAvailable(false); // Disable setup after successful admin creation
      } else {
        setMessage(result.message);
        setIsError(true);
      }
    } catch (error) {
      setMessage('An unexpected error occurred: ' + error.message);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToAdmin = () => {
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="admin-setup-container">
        <h1>Admin Setup</h1>
        <div className="admin-setup-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-setup-container">
      <h1>Admin Setup</h1>
      
      {!auth.currentUser ? (
        <div className="admin-setup-message error">
          <p>You need to be logged in to access this page.</p>
          <button onClick={() => navigate('/inloggen')} className="admin-setup-button">
            Go to Login
          </button>
        </div>
      ) : (
        <>
          <div className="admin-setup-user-info">
            <p>Logged in as: <strong>{auth.currentUser.email}</strong></p>
            <p>Admin status: <span className={adminStatus ? 'admin-status-yes' : 'admin-status-no'}>
              {adminStatus ? 'Yes' : 'No'}
            </span></p>
          </div>

          {message && (
            <div className={`admin-setup-message ${isError ? 'error' : 'success'}`}>
              {message}
            </div>
          )}

          {adminStatus ? (
            <div className="admin-setup-actions">
              <p>You already have admin privileges.</p>
              <button onClick={handleGoToAdmin} className="admin-setup-button">
                Go to Admin Dashboard
              </button>
            </div>
          ) : setupAvailable ? (
            <div className="admin-setup-actions">
              <p>You are setting up the first admin user for this system.</p>
              <p className="admin-setup-warning">
                <strong>Note:</strong> After you become an admin, this setup page will no longer be available.
                You'll need to use the admin dashboard to grant admin privileges to other users.
              </p>
              <button onClick={handleMakeAdmin} className="admin-setup-button" disabled={loading}>
                {loading ? 'Processing...' : 'Make Me Admin'}
              </button>
            </div>
          ) : (
            <div className="admin-setup-message error">
              <p>Admin setup is no longer available. Please contact an existing administrator to grant you admin privileges.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminSetup;